import React, { useState } from 'react';
import { Check, X, Loader, AlertCircle, Save } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

const SplunkWizard = ({ onClose, onSave }) => {
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState({
    url: '',
    token: '',
    index: 'security',
    sourcetype: 'sylink:scan',
    verify_ssl: true,
  });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      // First save config to backend
      const saveResponse = await fetch(`${API_BASE}/api/v1/system/integrations/splunk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({
          config: config,
          enabled: false
        }),
      });

      if (!saveResponse.ok) {
        const error = await saveResponse.json();
        throw new Error(error.detail || 'Erreur lors de la sauvegarde');
      }

      // Then test via backend API
      const response = await fetch(`${API_BASE}/api/v1/system/integrations/splunk/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      const result = await response.json();

      if (result.success) {
        setTestResult({ success: true, message: result.message || 'Splunk connection successful!' });
      } else {
        setTestResult({ success: false, message: result.error || 'Connection failed' });
      }
    } catch (error) {
      setTestResult({ success: false, message: error.message });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/system/integrations/splunk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({
          config: config,
          enabled: true
        }),
      });

      if (response.ok) {
        onSave('splunk', config);
        onClose();
      } else {
        const error = await response.json();
        setTestResult({ success: false, message: error.detail || 'Erreur lors de la sauvegarde' });
      }
    } catch (error) {
      setTestResult({ success: false, message: error.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div className="bg-brand-black-light rounded-xl border border-brand-green/30 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl">📡</span>
            <div>
              <h2 className="text-2xl font-bold text-brand-green">Configuration Splunk SIEM</h2>
              <p className="text-sm text-gray-400 mt-1">Step {step}/3</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">Informations Splunk</h3>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Splunk URL (avec port HEC)
                </label>
                <input
                  type="url"
                  value={config.url}
                  onChange={(e) => setConfig({ ...config, url: e.target.value })}
                  placeholder="https://splunk.exemple.com:8088"
                  className="input-field w-full"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Format: https://HOST:PORT (HEC port usually 8088)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  HEC Token
                </label>
                <input
                  type="password"
                  value={config.token}
                  onChange={(e) => setConfig({ ...config, token: e.target.value })}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  className="input-field w-full"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Token generated in Settings → Data Inputs → HTTP Event Collector
                </p>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">Configuration Index</h3>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Index Splunk
                </label>
                <input
                  type="text"
                  value={config.index}
                  onChange={(e) => setConfig({ ...config, index: e.target.value })}
                  placeholder="security"
                  className="input-field w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Index where events will be stored
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Source Type
                </label>
                <input
                  type="text"
                  value={config.sourcetype}
                  onChange={(e) => setConfig({ ...config, sourcetype: e.target.value })}
                  placeholder="sylink:scan"
                  className="input-field w-full"
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="verify_ssl"
                  checked={config.verify_ssl}
                  onChange={(e) => setConfig({ ...config, verify_ssl: e.target.checked })}
                  className="w-4 h-4 accent-brand-green"
                />
                <label htmlFor="verify_ssl" className="text-sm text-gray-300">
                  Verify SSL certificate
                </label>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">Test de connexion</h3>

              <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                <h4 className="text-sm font-medium text-gray-400 mb-3">Configuration</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">URL:</span>
                    <span className="text-white font-mono text-xs">{config.url || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Token:</span>
                    <span className="text-white font-mono">{'*'.repeat(20)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Index:</span>
                    <span className="text-white">{config.index}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Source Type:</span>
                    <span className="text-white">{config.sourcetype}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Verify SSL:</span>
                    <span className={config.verify_ssl ? 'text-brand-green' : 'text-orange-400'}>
                      {config.verify_ssl ? 'Oui' : 'Non'}
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleTest}
                disabled={testing || !config.url || !config.token}
                className="btn-green w-full flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {testing ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>Test en cours...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    <span>Tester la connexion</span>
                  </>
                )}
              </button>

              {testResult && (
                <div className={`p-4 rounded-lg border flex items-start gap-3 ${
                  testResult.success
                    ? 'bg-brand-green/10 border-brand-green/50'
                    : 'bg-red-500/10 border-red-500/50'
                }`}>
                  {testResult.success ? (
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    <div className={`font-medium ${testResult.success ? 'text-brand-green' : 'text-red-400'}`}>
                      {testResult.success ? 'Success' : 'Failure'}
                    </div>
                    <div className="text-sm text-gray-400 mt-1">{testResult.message}</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-800 flex items-center justify-between">
          <button
            onClick={() => setStep(Math.max(1, step - 1))}
            disabled={step === 1}
            className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            Previous
          </button>

          <div className="flex items-center gap-2">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full ${
                  i === step ? 'bg-brand-green' : i < step ? 'bg-brand-green/50' : 'bg-gray-700'
                }`}
              />
            ))}
          </div>

          {step < 3 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={step === 1 && (!config.url || !config.token)}
              className="btn-green disabled:opacity-50"
            >
              Suivant
            </button>
          ) : (
            <button
              onClick={handleSave}
              disabled={!testResult?.success || saving}
              className="btn-green disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>Sauvegarde...</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>Sauvegarder & Activer</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SplunkWizard;
